/**
 * Governance Dashboard Page - Sprint 15
 * GC-7: Analytics dashboard for governance metrics
 * 
 * Features:
 * - KPI cards (approval rate, SLA compliance, avg time, etc.)
 * - Line charts for trends
 * - Bar charts for bottlenecks
 * - Donut charts for ratios
 * - Tables for audit trail and variance
 * - Export PDF/CSV
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle, XCircle, Clock, AlertTriangle, TrendingUp, TrendingDown,
  Download, RefreshCw, Filter, Calendar, FileText, DollarSign, Users,
  Loader2, ChevronDown, BarChart3, PieChart, Activity, AlertCircle,
  ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// KPI Card Component
const KPICard = ({ title, value, unit, trend, trendValue, icon: Icon, color }) => {
  const trendColors = {
    up: 'text-green-600',
    down: 'text-red-600',
    stable: 'text-gray-500'
  };
  
  return (
    <div className="bg-white rounded-lg border p-4 shadow-sm" data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm text-gray-500">{title}</p>
          <div className="flex items-baseline gap-1 mt-1">
            <span className="text-2xl font-bold text-gray-900">{value}</span>
            {unit && <span className="text-sm text-gray-500">{unit}</span>}
          </div>
          {trend && (
            <div className={`flex items-center gap-1 mt-1 text-sm ${trendColors[trend]}`}>
              {trend === 'up' && <ArrowUpRight className="w-4 h-4" />}
              {trend === 'down' && <ArrowDownRight className="w-4 h-4" />}
              {trend === 'stable' && <Activity className="w-4 h-4" />}
              <span>{trendValue > 0 ? '+' : ''}{trendValue}</span>
            </div>
          )}
        </div>
        <div className={`p-2 rounded-lg ${color || 'bg-blue-50'}`}>
          <Icon className={`w-5 h-5 ${color?.includes('green') ? 'text-green-600' : color?.includes('red') ? 'text-red-600' : 'text-blue-600'}`} />
        </div>
      </div>
    </div>
  );
};

// Donut Chart Component
const DonutChart = ({ data, title, centerLabel }) => {
  const COLORS = ['#10B981', '#EF4444', '#F59E0B', '#6366F1'];
  
  return (
    <div className="bg-white rounded-lg border p-4">
      <h3 className="text-sm font-medium text-gray-700 mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height={200}>
        <RechartsPie>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={50}
            outerRadius={70}
            paddingAngle={2}
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </RechartsPie>
      </ResponsiveContainer>
      {centerLabel && (
        <div className="text-center -mt-20 relative z-10">
          <span className="text-2xl font-bold text-gray-900">{centerLabel}</span>
        </div>
      )}
    </div>
  );
};

// Breach Alert Item
const BreachAlertItem = ({ item }) => {
  const urgencyColor = item.hours_remaining < 12 ? 'bg-red-50 border-red-200' : 
                       item.hours_remaining < 24 ? 'bg-amber-50 border-amber-200' : 
                       'bg-yellow-50 border-yellow-200';
  
  return (
    <div className={`p-3 rounded-lg border ${urgencyColor}`}>
      <div className="flex items-start justify-between">
        <div>
          <h4 className="font-medium text-gray-900 text-sm">{item.title}</h4>
          <p className="text-xs text-gray-500 mt-0.5">
            Stage: {item.current_stage?.replace(/_/g, ' ')} • {item.department}
          </p>
        </div>
        <span className={`text-xs font-medium px-2 py-1 rounded ${
          item.hours_remaining < 12 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
        }`}>
          {item.hours_remaining}h left
        </span>
      </div>
    </div>
  );
};

// Variance Insight Row
const VarianceRow = ({ insight }) => {
  const isPositive = insight.variance_amount >= 0;
  
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3 text-sm text-gray-900">{insight.title}</td>
      <td className="px-4 py-3 text-sm">
        <span className={isPositive ? 'text-red-600' : 'text-green-600'}>
          {isPositive ? '+' : ''}{insight.variance_amount?.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-500">{insight.variance_pct}%</td>
      <td className="px-4 py-3 text-sm">
        <span className={`px-2 py-0.5 rounded text-xs ${
          insight.impact === 'high' || insight.impact === 'critical' ? 'bg-red-100 text-red-700' :
          insight.impact === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
        }`}>
          {insight.impact}
        </span>
      </td>
    </tr>
  );
};

// Main Component
const GovernanceDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    stage: '',
    department: '',
    dateFrom: '',
    dateTo: ''
  });
  const [exporting, setExporting] = useState(false);
  
  // Load dashboard data
  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.stage) params.append('stage_filter', filters.stage);
      if (filters.department) params.append('department_filter', filters.department);
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);
      
      const res = await axios.get(
        `${API_BASE}/analytics/governance-dashboard?${params}`,
        { headers: getAuthHeaders() }
      );
      
      setDashboard(res.data.dashboard);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      toast.error('Failed to load governance dashboard');
    } finally {
      setLoading(false);
    }
  }, [filters]);
  
  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);
  
  // Export handlers
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const res = await axios.get(
        `${API_BASE}/analytics/governance-dashboard/export/csv`,
        { headers: getAuthHeaders(), responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `governance_dashboard_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('CSV exported successfully');
    } catch (err) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };
  
  const handleExportPDF = async (mode = 'full') => {
    setExporting(true);
    try {
      const res = await axios.get(
        `${API_BASE}/analytics/governance-dashboard/export/pdf?mode=${mode}`,
        { headers: getAuthHeaders() }
      );
      
      toast.success('PDF export prepared. Check download URL in response.');
      console.log('PDF Export Info:', res.data);
    } catch (err) {
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }
  
  const { summary_kpis, approval_rates, time_in_stage, sla_compliance, bottlenecks, throughput, variance_insights, upcoming_breaches } = dashboard || {};
  
  // Prepare chart data
  const throughputChartData = throughput?.weekly_data?.slice(-8) || [];
  const slaChartData = sla_compliance?.trend?.slice(-8) || [];
  
  const approvalDonutData = [
    { name: 'Approved', value: approval_rates?.period_30d?.approved || 0 },
    { name: 'Rejected', value: approval_rates?.period_30d?.rejected || 0 },
    { name: 'Pending', value: approval_rates?.period_30d?.pending || 0 }
  ];
  
  return (
    <div className="min-h-screen bg-gray-100 p-6" data-testid="governance-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Governance Dashboard</h1>
          <p className="text-gray-500">Analytics and insights for governance workflows</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm ${showFilters ? 'bg-blue-50 border-blue-300' : ''}`}
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
          
          <div className="relative group">
            <button
              className="flex items-center gap-2 px-3 py-2 border rounded-lg text-sm hover:bg-gray-50"
              disabled={exporting}
              data-testid="export-btn"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export
              <ChevronDown className="w-4 h-4" />
            </button>
            <div className="absolute right-0 mt-1 w-48 bg-white border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
              <button
                onClick={handleExportCSV}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                Export CSV
              </button>
              <button
                onClick={() => handleExportPDF('full')}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                Export PDF (Full)
              </button>
              <button
                onClick={() => handleExportPDF('data-only')}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                Export PDF (Tables Only)
              </button>
            </div>
          </div>
          
          <button
            onClick={loadDashboard}
            className="flex items-center gap-2 px-3 py-2 border rounded-lg text-sm hover:bg-gray-50"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>
      
      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-lg border p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Stage</label>
              <select
                value={filters.stage}
                onChange={(e) => setFilters({ ...filters, stage: e.target.value })}
                className="border rounded px-3 py-2 text-sm"
                data-testid="filter-stage"
              >
                <option value="">All Stages</option>
                <option value="intake_review">Intake Review</option>
                <option value="assessment_review">Assessment Review</option>
                <option value="governance_board">Governance Board</option>
              </select>
            </div>
            
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Department</label>
              <select
                value={filters.department}
                onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                className="border rounded px-3 py-2 text-sm"
                data-testid="filter-department"
              >
                <option value="">All Departments</option>
                <option value="Engineering">Engineering</option>
                <option value="Product">Product</option>
                <option value="Operations">Operations</option>
                <option value="Finance">Finance</option>
              </select>
            </div>
            
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">From Date</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                className="border rounded px-3 py-2 text-sm"
              />
            </div>
            
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">To Date</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                className="border rounded px-3 py-2 text-sm"
              />
            </div>
            
            {(filters.stage || filters.department || filters.dateFrom || filters.dateTo) && (
              <button
                onClick={() => setFilters({ stage: '', department: '', dateFrom: '', dateTo: '' })}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <KPICard
          title="Approval Rate"
          value={summary_kpis?.approval_rate_30d || 0}
          unit="%"
          trend="up"
          trendValue={5.2}
          icon={CheckCircle}
          color="bg-green-50"
        />
        <KPICard
          title="SLA Compliance"
          value={summary_kpis?.sla_compliance || 0}
          unit="%"
          trend="stable"
          trendValue={0.5}
          icon={Clock}
          color="bg-blue-50"
        />
        <KPICard
          title="Avg Time to Approve"
          value={summary_kpis?.avg_time_to_approval || 0}
          unit="hrs"
          trend="down"
          trendValue={-2.1}
          icon={Activity}
          color="bg-purple-50"
        />
        <KPICard
          title="Pending Items"
          value={summary_kpis?.total_pending || 0}
          trend="up"
          trendValue={3}
          icon={AlertTriangle}
          color="bg-amber-50"
        />
        <KPICard
          title="Upcoming Breaches"
          value={summary_kpis?.upcoming_breaches || 0}
          trend="stable"
          trendValue={0}
          icon={AlertCircle}
          color="bg-red-50"
        />
      </div>
      
      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Decision Throughput Line Chart */}
        <div className="lg:col-span-2 bg-white rounded-lg border p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-4">Decision Throughput (Weekly)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={throughputChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" fontSize={12} />
              <YAxis fontSize={12} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="approved" stroke="#10B981" strokeWidth={2} name="Approved" />
              <Line type="monotone" dataKey="rejected" stroke="#EF4444" strokeWidth={2} name="Rejected" />
              <Line type="monotone" dataKey="total" stroke="#6366F1" strokeWidth={2} strokeDasharray="5 5" name="Total" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        
        {/* Approval Ratio Donut */}
        <DonutChart
          data={approvalDonutData}
          title="Approval Ratio (30 Days)"
          centerLabel={`${approval_rates?.period_30d?.approval_rate || 0}%`}
        />
      </div>
      
      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* SLA Compliance Trend */}
        <div className="bg-white rounded-lg border p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-4">SLA Compliance Trend</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={slaChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" fontSize={12} />
              <YAxis fontSize={12} domain={[70, 100]} />
              <Tooltip />
              <Line type="monotone" dataKey="compliance_rate" stroke="#3B82F6" strokeWidth={2} name="Compliance %" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        
        {/* Bottlenecks by Stage Bar Chart */}
        <div className="bg-white rounded-lg border p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-4">Bottlenecks by Stage</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={bottlenecks?.by_stage || []} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" fontSize={12} />
              <YAxis dataKey="stage" type="category" fontSize={11} width={120} />
              <Tooltip />
              <Bar dataKey="count" fill="#F59E0B" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Financial Variance Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Top Variance Drivers
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Decision</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Variance</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">%</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Impact</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {variance_insights?.slice(0, 5).map((insight, idx) => (
                  <VarianceRow key={idx} insight={insight} />
                ))}
                {(!variance_insights || variance_insights.length === 0) && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                      No variance data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Upcoming SLA Breaches */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Upcoming SLA Breaches (48h)
            </h3>
          </div>
          <div className="p-4 space-y-3 max-h-[300px] overflow-y-auto">
            {upcoming_breaches?.map((item, idx) => (
              <BreachAlertItem key={idx} item={item} />
            ))}
            {(!upcoming_breaches || upcoming_breaches.length === 0) && (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                <p>No upcoming SLA breaches</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Bottlenecks by Department */}
      <div className="bg-white rounded-lg border p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-4">Bottlenecks by Department</h3>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={bottlenecks?.by_department || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="department" fontSize={12} />
            <YAxis fontSize={12} />
            <Tooltip />
            <Bar dataKey="count" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        
        {/* Recommendations */}
        {bottlenecks?.recommendations?.length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Recommendations</h4>
            <ul className="space-y-1">
              {bottlenecks.recommendations.map((rec, idx) => (
                <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                  <TrendingUp className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  {rec}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default GovernanceDashboard;
